// import GLBench from 'gl-bench';
import GLBench from 'gl-bench/dist/gl-bench';
import { Interaction } from '../../../vendors/three.interaction/build/three.interaction.class.module';
import GradientBackground from './objects/GradientBackground';
import { prepareInfrastructure } from './init/PrepareInfrastructure';
import { SIZES_COMMON } from '../../constants/sizes';
import THREE from './utils/threeClassesAdapter';

const addBenchMonitor = (renderer) => {
    const bench = new GLBench(renderer.getContext());
    const draw = (now) => {
        bench.begin();
        // monitored code
        bench.end();
        bench.nextFrame(now);
    };

    renderer.setAnimationLoop((now) => draw(now));
};

const constructGradientBg = (renderer) => {
    const infrastructure = prepareInfrastructure(renderer, null, {
        far: 100
    });
    const bgObject3D = new GradientBackground();

    infrastructure.camera.position.z = 1;
    bgObject3D.position.z = -1;
    infrastructure.scene.add(bgObject3D);

    return {
        bgInfrastructure: infrastructure,
        bgObject3D
    };
};

export class ThreeShell {
    #hasRenderer = false;
    #renderSequence = [];
    #renderer;
    #mainInfrastructure;
    #bgInfrastructure;
    #bgObject3D;
    #root3D;
    #rig;

    constructor(domElement, monitorGPUandCPU = false) {
        const interactionIsOn = true;

        // CONSTRUCT
        // 1. Add main infrastructure
        const mainInfrastructure = prepareInfrastructure(
            null,
            domElement,
            {
                near: SIZES_COMMON.camFar * 0.1,
                far: SIZES_COMMON.camFar
            },
            window.innerWidth,
            window.innerHeight,
            false,
            false,
            false,
            false
        );
        const { renderer } = mainInfrastructure;

        if (renderer) {
            //
            // 2. Add root group to the main scene
            const { scene, camera, rig } = mainInfrastructure;
            const root3D = new THREE.Group();

            scene.add(root3D);

            // 3. ADD Gradient BG and its own infrastructure
            const {
                bgInfrastructure,
                bgObject3D
            } = constructGradientBg(renderer);

            // 4. Add interaction to the main scene
            if (interactionIsOn) {
                const interaction = new Interaction(renderer, scene, camera);

                // IMPORTANT! Need style correction to enable touch devices content scroll
                // touch-action: auto;
                interaction.renderer.domElement.style.touchAction = 'auto';

                // Layers:
                // 0 - static layers. Default
                // 1 - interactive layers
                camera.layers.enable(0);
                camera.layers.enable(1);
                interaction.raycaster.layers.disable(0);
                interaction.raycaster.layers.enable(1);

                this.interaction = interaction;
            }

            // Monitor if necessary
            if (monitorGPUandCPU) addBenchMonitor(renderer);

            this.#hasRenderer = true;
            this.#renderer = renderer;
            this.#mainInfrastructure = mainInfrastructure;
            this.#bgInfrastructure = bgInfrastructure;
            this.#bgObject3D = bgObject3D;
            this.#root3D = root3D;
            this.#rig = rig;
            this.#renderSequence = [bgInfrastructure, mainInfrastructure];
        }
    }

    render() {
        this.#renderSequence.forEach((ifs) => ifs.render());
    }

    resize() {
        if (!this.#hasRenderer) return;

        const { camera } = this.#mainInfrastructure;

        const winW = window.innerWidth;
        const winH = window.innerHeight;

        //
        // MAIN:
        const pixelRatio = this.#renderer.getPixelRatio();

        // - camera
        // - camera
        let fov = 35;

        if (winW < winH) {
            fov = (45 * winH) / Math.min(winW * 1.5, winH);
        }

        camera.fov = fov;
        camera.aspect = winW / winH;
        camera.updateProjectionMatrix();

        this.#rig.isPortrait = !(camera.aspect > 1);

        //
        // BG:

        // - camera
        this.#bgInfrastructure.camera.aspect = camera.aspect;
        this.#bgInfrastructure.camera.updateProjectionMatrix();

        // - gradient size
        this.#bgObject3D.resolution = new THREE.Vector2(winW * pixelRatio, winH * pixelRatio);

        // COMMON:
        this.#renderer.setSize(winW, winH);
    }

    invalidate(/* t, dt */) {
        if (!this.#hasRenderer) return;

        // BG animation
        // this.#bgObject3D.invalidate(t, dt);

        const { scene } = this.#mainInfrastructure;

        this.#rig.invalidate();

        scene.updateMatrixWorld();
    }

    destroy() {
        if (!this.#hasRenderer) return;

        this.rig.destroy();
        this.#renderer.dispose();

        this.#renderSequence = [];
        this.#renderer = undefined;
        this.#mainInfrastructure = undefined;
        this.#bgInfrastructure = undefined;
        this.#bgObject3D = undefined;
        this.#root3D = undefined;

        this.#hasRenderer = false;
    }

    add(object3D) {
        if (!this.#hasRenderer) return null;

        return this.#root3D.add(object3D);
    }

    remove(object3D) {
        if (!this.#hasRenderer) return null;

        return this.#root3D.remove(object3D);
    }

    get bgObject3D() {
        return this.#bgObject3D;
    }

    get infrastructure() {
        return this.#mainInfrastructure;
    }

    get hasRenderer() {
        return this.#hasRenderer;
    }
}
